import { useState } from "react";
import { ERROR_MESSAGES } from "../../../constant/constant";
import { TENANT_DOMAIN } from "../../../utils/domain";
import { useAxios } from "../../../utils/useAxios";

/**
 * @typedef {object} TeacherDetail
 * @property {string} name - Name of the teacher
 * @property {string|null} email - Email of the teacher
 * @property {number} total_classes - Total number of classes
 * @property {number} overall_avg_progress - Overall average progress
 * @property {number} overall_quiz_pass_rate - Overall quiz pass rate
 * @property {number} completion_rate - Completion rate
 */

/**
 * @typedef {object} ClassItem
 * @property {string} class_name - Class name
 * @property {number} progress_lt_50 - Students with progress less than 50%
 * @property {number} progress_gt_50 - Students with progress greater than 50%
 * @property {string} quiz_performance - Quiz performance string (e.g., "Passed: 0% | Failed: 100%")
 * @property {number} avg_attempts - Average attempts
 */

/**
 * @typedef {object} TeacherReportByIdData
 * @property {TeacherDetail} teacher - Teacher details
 * @property {ClassItem[]} classes - List of classes taught by the teacher
 */

/**
 * Custom hook to fetch teacher report by school ID and teacher ID
 */
export const useGetTeacherReportById = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const { axiosInstance } = useAxios();

    const [reportData, setReportData] = useState(
        /** @type {TeacherReportByIdData | null | undefined} */(undefined)
    );

    /**
     * Fetch teacher report by school ID and teacher ID
     * @async
     * @param {number|string} schoolId - ID of the sub-school
     * @param {number|string} teacherId - ID of the teacher
     * @returns {Promise<void>}
     */
    const getTeacherReportById = async (schoolId, teacherId) => {
        if (!schoolId || !teacherId) {
            setError("School ID and Teacher ID are required");
            setLoading(false);
            return;
        }

        setLoading(true);
        setError("");
        try {
            const url = `${TENANT_DOMAIN}/organization/admin/reports/sub-school/${schoolId}/teacher/${teacherId}`;
            const response = await axiosInstance.get(url);
            const data = response.data?.data;

            setReportData(data);
        } catch (err) {
            setReportData(null);
            if (err?.response?.status === 500) {
                setError(ERROR_MESSAGES.INTERNAL_SERVER);
            } else {
                setError(err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN);
            }
        } finally {
            setLoading(false);
        }
    };

    return { loading, error, reportData, getTeacherReportById };
};